import { StatusDot, StatusLineShell } from '../table-session/StatusLine';
import { SEAT_NAMES, isSeat } from '../table-session/SeatLabel';
import { ConceptualEditorPill } from './ConceptualEditorPill';
import type { ConceptualProblem } from '../../types/conceptualProblems';

interface ConceptualStatusLineProps {
  problem: ConceptualProblem;
  /** 1-based position in the category. */
  position: number;
  total: number;
  /**
   * The editor has just re-filed this problem out of the category they are browsing (FR-032).
   *
   * <p>The page keeps it on screen rather than making it vanish mid-sentence, which means the
   * screen would otherwise be quietly lying: this is a problem the category no longer contains.
   * The note is what makes that honest, and it is why the problem staying put is a kindness rather
   * than a bug.</p>
   */
  movedOut?: boolean;
}

/**
 * The line beneath the frame (feature 029) — what this problem is, and where in the category it
 * sits.
 *
 * <p>It is built on the shared <code>StatusLineShell</code>, so it reads identically to the
 * practice pages' line without re-deriving their spacing, type scale or cream-on-felt colour, and
 * inherits `data-testid="status-line"` — which is what lets the shared e2e layout battery assert
 * this page.</p>
 *
 * <p>What it carries differs, because nothing here is answered: there is no streak, no score and
 * no review pin. In their place go the facts an editor is actually checking — the serial they will
 * quote in a report, the difficulty and scoring as recorded, the seat being asked, the topics it is
 * filed under, and the component tags, which are shown verbatim because normalising their wording
 * would hide the very inconsistencies this page exists to reveal.</p>
 *
 * <p><b>The categories read the same however they got there.</b> The database records whether each
 * assignment was proposed by the import's classifier or chosen by an editor, and this line does not
 * distinguish them — the client asked for that on 2026-09-10 (FR-033, FR-037). The reason is not
 * cosmetic: marking a row as "proposed" invites an editor to treat a machine's guess as a second
 * opinion worth deferring to, when the whole point of this page is that a person decides.</p>
 */
export function ConceptualStatusLine({
  problem,
  position,
  total,
  movedOut = false,
}: ConceptualStatusLineProps) {
  return (
    <StatusLineShell>
      <span data-testid="conceptual-serial">Serial: {problem.serial}</span>

      <StatusDot />

      <span>
        Problem <span data-testid="conceptual-position">{position} of {total}</span>
      </span>

      {problem.difficultyLevel != null && (
        <>
          <StatusDot />
          <span data-testid="conceptual-difficulty">Difficulty {problem.difficultyLevel}/10</span>
        </>
      )}

      <StatusDot />

      <span data-testid="conceptual-scoring">{problem.scoring || 'N/A'}</span>

      {isSeat(problem.decisionSeat) && (
        <>
          <StatusDot />
          <span data-testid="conceptual-seat">Asked of: {SEAT_NAMES[problem.decisionSeat]}</span>
        </>
      )}

      <StatusDot />

      {/* "Uncategorised" when there are none — a real state an editor can choose, not a gap, so it
          is stated rather than left blank (FR-033). */}
      {/* NOT `conceptual-categories`: that hook belongs to the category LIST's grid. No single
          page renders both, but a helper written against either would match the wrong one, which
          is the same trap the details popup avoided with its own testId. */}
      <span data-testid="conceptual-problem-categories">
        {problem.categories.length > 0
          ? problem.categories.map((c) => c.name).join(', ')
          : 'Uncategorised'}
      </span>

      {/* Whose decision this is (FR-047), and whether a Super Editor has settled it (FR-048). The
          line has room for the whole name, so wider screens get it in full; phones get the short
          form in the masthead pill instead. Absent on a problem no editor has saved. */}
      {problem.lastEdit && (
        <>
          <StatusDot />
          {/* The phone masthead's brass pill, in full wording: as a plain label it was lost among
              the cream facts beside it (client, 2026-09-17). */}
          <ConceptualEditorPill lastEdit={problem.lastEdit} locked={problem.locked} variant="status" />
        </>
      )}

      {movedOut && (
        <>
          <StatusDot />
          <span className="font-normal italic" data-testid="conceptual-moved-out">
            No longer in this category
          </span>
        </>
      )}

      {problem.components.length > 0 && (
        <span className="flex flex-wrap items-center gap-1" data-testid="conceptual-components">
          {problem.components.map(tag => (
            <span
              key={tag}
              className="rounded-full border border-brass bg-cream px-2.5 py-0.5 text-[12.5px] font-bold text-brass-text"
            >
              {tag}
            </span>
          ))}
        </span>
      )}

      {/* A source title runs long enough to push everything else off the line, so it takes a row of
          its own — exactly as the practice line does with its tournament title. */}
      {problem.source && (
        <span className="basis-full font-normal" data-testid="conceptual-source">
          From: {problem.source}
        </span>
      )}
    </StatusLineShell>
  );
}
